import React from 'react';
import { useRownd } from '@rownd/react';
import { Typography, Button, Container, Grid, Card, CardContent, Box } from '@mui/material';
import { Security, Code, Payments } from '@mui/icons-material';

const HomePage: React.FC = () => {
  const { requestSignIn } = useRownd();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200">
      {/* Hero Section */}
      <Box className="py-20 bg-gradient-to-r from-purple-700 to-blue-500 text-white">
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" className="font-bold mb-4">
                VIBE Coding with Rownd
              </Typography>
              <Typography variant="h6" className="mb-6">
                Elevate your AI-assisted coding experience with secure authentication that just works.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => requestSignIn()}
                className="bg-white text-purple-700 hover:bg-gray-100"
              >
                Get Started Today
              </Button>
            </Grid>
            <Grid item xs={12} md={6} className="flex justify-center">
              <img 
                src="https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80" 
                alt="Vibe Coding" 
                className="rounded-lg shadow-xl max-w-full h-auto"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What is Vibe Coding Section */}
      <Container maxWidth="lg" className="py-16">
        <Typography variant="h3" component="h2" className="text-center mb-12 font-bold text-gray-800">
          What is VIBE Coding?
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" className="text-gray-700 mb-4">
              VIBE Coding (Very Intelligent Bot-Enhanced Coding) is an AI-dependent programming technique where 
              developers describe problems to large language models (LLMs) which then generate code solutions.
            </Typography>
            <Typography variant="body1" className="text-gray-700 mb-4">
              This shifts the programmer's role from manual coding to guiding, testing, and refining AI-generated code, 
              creating a collaborative workflow between human creativity and AI capabilities.
            </Typography>
            <Typography variant="body1" className="text-gray-700">
              With VIBE Coding, developers can focus on higher-level concerns such as architecture, user experience, 
              and business logic while the AI handles repetitive coding tasks.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} className="flex items-center justify-center">
            <Card className="max-w-md w-full shadow-lg">
              <CardContent>
                <Typography variant="h5" component="h3" className="font-bold mb-4">
                  Key Benefits of VIBE Coding
                </Typography>
                <ul className="space-y-2 pl-5 list-disc text-gray-700">
                  <li>Dramatically increased development speed</li>
                  <li>Reduced cognitive load for repetitive tasks</li>
                  <li>Ability to explore multiple solutions quickly</li>
                  <li>Accessible coding for non-expert programmers</li>
                  <li>Focus on problem-solving rather than syntax</li>
                  <li>Better documentation through conversation</li>
                </ul>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Why Rownd Section */}
      <Box className="py-16 bg-gray-800 text-white">
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" className="text-center mb-12 font-bold">
            Why Rownd for VIBE Coding?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card className="h-full bg-gray-700 text-white">
                <CardContent className="flex flex-col items-center text-center">
                  <Security className="text-purple-400 text-5xl mb-4" />
                  <Typography variant="h5" component="h3" className="font-bold mb-2">
                    Secure Authentication
                  </Typography>
                  <Typography variant="body1">
                    Rownd provides enterprise-grade authentication that's easy to implement. 
                    Protect your VIBE coding project with passwordless sign-in, multi-factor 
                    authentication, and robust security protocols.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card className="h-full bg-gray-700 text-white">
                <CardContent className="flex flex-col items-center text-center">
                  <Code className="text-purple-400 text-5xl mb-4" />
                  <Typography variant="h5" component="h3" className="font-bold mb-2">
                    Easy Integration
                  </Typography>
                  <Typography variant="body1">
                    Implement authentication in minutes, not days. Rownd's simple SDK lets you 
                    focus on VIBE coding your core product features while we handle the complex 
                    user management infrastructure.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card className="h-full bg-gray-700 text-white">
                <CardContent className="flex flex-col items-center text-center">
                  <Payments className="text-purple-400 text-5xl mb-4" />
                  <Typography variant="h5" component="h3" className="font-bold mb-2">
                    Monetization Ready
                  </Typography>
                  <Typography variant="body1">
                    Turn your VIBE code project into a money-maker with user management that's 
                    ready for subscription models. Collect user data securely and seamlessly 
                    for your SaaS business model.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container maxWidth="md" className="py-16 text-center">
        <Typography variant="h4" component="h2" className="mb-6 font-bold text-gray-800">
          Ready to elevate your VIBE Coding with secure authentication?
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          onClick={() => requestSignIn()}
          className="bg-purple-600 hover:bg-purple-700"
        >
          Sign Up Now - Just $1/month while vibing
        </Button>
      </Container>
    </div>
  );
};

export default HomePage; 